import { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useCalibration } from '../contexts/CalibrationContext';
import { useAuth } from '../contexts/AuthContext';
import { useBrainBarContext } from '../contexts/BrainBarContext';
import { Brand } from './Brand';
import { NavIcon } from './NavIcon';
import type { IconName } from './NavIcon';
import { BrainOrb, CommandBar, useBrainBar } from './primitives';
import type { Command } from './primitives';
import { SHELL_COMMANDS, resolveShellCommands } from '../lib/brainBarDefaults';
import { isPlaceholderSupabase } from '../lib/supabase';
import { BrainChatPanel } from './BrainChatPanel';
import { NowSurface } from './NowSurface';
import { CoachReviewBanner } from './CoachReviewBanner';
import { useCoachReviewMode } from '../hooks/useCoachReviewMode';
import { FirstRunTour } from './firstRun/FirstRunTour';
import './AppShell.css';

interface NavItemDef {
  to: string;
  icon: IconName;
  label: string;
  end?: boolean;
  badge?: string;
}

// Sprint 1 IA reshape (2026-05-07) — Brain at the centre. Sidebar drops
// from 9–11 items to 4–5. Sessions / Games / Players / Drills / Clips /
// Principles / Curriculum / 3D Pitch all stay routable, but they're
// destinations Brain dispatches the coach to (via chat deep-links + the
// CommandBar), not nav-pinned. Reviews surfaces only at academy where the
// reflection loop matters more.
// Sessions + Drills surface explicitly so pro-coach reviewers (and any
// first-time coach) can see "where do I find my sessions" and "where's
// my drill library" without having to learn Brain dispatch first. They
// stay routable via Brain too — the sidebar entries are an additional
// path, not a replacement.
const NOVICE_NAV: NavItemDef[] = [
  { to: '/', icon: 'today', label: 'Today', end: true, badge: '3' },
  { to: '/sessions', icon: 'sessions', label: 'Sessions' },
  { to: '/drills', icon: 'drills', label: 'Drills' },
  { to: '/tactics', icon: 'tactics', label: 'Tactics' },
  { to: '/calendar', icon: 'calendar', label: 'Calendar' },
];

const ACADEMY_NAV: NavItemDef[] = [
  ...NOVICE_NAV,
  { to: '/reviews', icon: 'reviews', label: 'Reviews' },
];

// Pro-coach reviewers (Ben et al) get a deliberately tiny sidebar — just
// the two surfaces they're here to evaluate. Everything else stays routable
// via Brain + the CommandBar, but isn't pinned, so the shell stays calm and
// unintimidating for a non-technical senior coach.
const REVIEW_NAV: NavItemDef[] = [
  { to: '/sessions', icon: 'sessions', label: 'Sessions' },
  { to: '/drills', icon: 'drills', label: 'Drills' },
];

export function AppShell() {
  const { level, setLevel } = useCalibration();
  const { user } = useAuth();
  const { pageCommands } = useBrainBarContext();
  const { open, setOpen } = useBrainBar();
  const navigate = useNavigate();
  // Review coaches (Ben et al) get a clearer "Ask Brain" label since
  // they're voice-first + non-technical.
  const { isReviewMode, firstRunCompletedAt, loading: reviewLoading, coachName } = useCoachReviewMode();
  const location = useLocation();
  // Latch the replay request the first time we see `?replay=1` in the
  // URL — the tour navigates between routes during its steps, which
  // strips the query param. Without latching, the tour would unmount
  // mid-tour the moment Step 2 navigates away.
  const [replayLatched, setReplayLatched] = useState(false);
  useEffect(() => {
    if (new URLSearchParams(location.search).get('replay') === '1') {
      setReplayLatched(true);
    }
  }, [location.search]);
  // Once the tour mounts, keep it mounted until firstRunCompletedAt
  // flips. Prevents the brief `loading=true` flicker during route
  // navigation from unmounting + remounting the tour (which resets
  // the step index to 0).
  const [tourStuck, setTourStuck] = useState(false);
  const rawShow = !reviewLoading && isReviewMode && (!firstRunCompletedAt || replayLatched);
  useEffect(() => {
    if (rawShow) setTourStuck(true);
  }, [rawShow]);
  // Clear the stick when the tour completes (firstRunCompletedAt flips).
  useEffect(() => {
    if (firstRunCompletedAt && !replayLatched && tourStuck) {
      const timer = setTimeout(() => setTourStuck(false), 100);
      return () => clearTimeout(timer);
    }
  }, [firstRunCompletedAt, replayLatched, tourStuck]);
  const showFirstRunTour = tourStuck && isReviewMode && (!firstRunCompletedAt || replayLatched);

  const isAcademy = level === 'academy';
  const nav = isReviewMode ? REVIEW_NAV : isAcademy ? ACADEMY_NAV : NOVICE_NAV;
  const offline = isPlaceholderSupabase();

  // Brain chat panel — global "ask anything" surface, opens with a seed
  // message when the coach types a query into CommandBar that doesn't
  // match a command (escalation path).
  const [chatOpen, setChatOpen] = useState(false);
  const [chatSeed, setChatSeed] = useState<string | null>(null);
  const openChatWith = (seed: string | null) => {
    setChatSeed(seed);
    setChatOpen(true);
  };

  // NowSurface — "what's ready right now" sheet. Opens via the
  // 'now-surface:open' window event so any page (or CommandBar entry)
  // can trigger it without prop-drilling.
  const [nowOpen, setNowOpen] = useState(false);
  useEffect(() => {
    const open = () => setNowOpen(true);
    window.addEventListener('now-surface:open', open);
    return () => window.removeEventListener('now-surface:open', open);
  }, []);

  // Global 'brain-chat:open' event — any page can dispatch this with an
  // optional `detail.seed` to pop the Brain chat panel pre-seeded with a
  // prompt. Used by the "Ask Brain" buttons on Drills + SessionDetail.
  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<{ seed?: string | null }>;
      const seed = ce.detail?.seed ?? null;
      setChatSeed(seed);
      setChatOpen(true);
    };
    window.addEventListener('brain-chat:open', handler);
    return () => window.removeEventListener('brain-chat:open', handler);
  }, []);

  const askBrainCommand: Command = useMemo(() => ({
    family: 'ask',
    label: 'Ask Brain anything…',
    hint: 'open chat',
    onRun: () => openChatWith(null),
  }), []);

  const commands = useMemo<Command[]>(
    () => [askBrainCommand, ...pageCommands, ...resolveShellCommands(SHELL_COMMANDS, navigate)],
    [askBrainCommand, pageCommands, navigate],
  );

  const initials = (user?.email ?? 'C').slice(0, 2).toUpperCase();
  // In review mode, don't show the demo "Aurora" club chrome — it's
  // misleading for Ben. Show his own review context instead.
  const teamLabel = isReviewMode ? 'Review session' : isAcademy ? 'Aurora · U17 Academy' : 'Aurora · U12';
  const brandTeam = isReviewMode
    ? (coachName ? `${coachName} · Review` : 'Review session')
    : isAcademy ? 'Aurora FC · U17 Academy' : 'Aurora FC · U12';

  return (
    <div className="shell" data-level={level} data-offline={offline || undefined}>
      <header className="status">
        <div className="status__left">
          <Brand team={brandTeam} />
          <span className="crumb">
            <span className="crumb__sep">/</span>
            <span className="crumb__now">Today</span>
          </span>
          {offline && (
            <span
              className="offline-pill"
              role="status"
              aria-label="Offline mode — using saved data on this device"
              title="No backend configured. Onboarding + sessions persist on this device only."
            >
              <span className="offline-pill__dot" /> offline · saved on device
            </span>
          )}
        </div>
        <div className="status__right">
          <span className="ai-dot ai-dot--orb">
            <BrainOrb size="xs" ariaLabel="Brain status" />
            Brain idle · 0 pending
          </span>
          {!isReviewMode && (
            <button
              type="button"
              className="level-toggle"
              onClick={() => setLevel(level === 'novice' ? 'academy' : 'novice')}
              aria-label={`Calibration: ${level} — tap to flip`}
            >
              <span data-active={!isAcademy}>novice</span>
              <span data-active={isAcademy}>academy</span>
            </button>
          )}
          <span className="kbd">⌘K</span>
        </div>
      </header>

      <aside className="sidebar">
        <div className="nav-section">Workflow</div>
        <nav className="nav-list">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-nav-item={item.icon}
              className={({ isActive }) =>
                isActive ? 'nav-item nav-item--active' : 'nav-item'
              }
            >
              <NavIcon name={item.icon} />
              <span className="nav-item__label">{item.label}</span>
              {item.badge && <span className="nav-badge">{item.badge}</span>}
            </NavLink>
          ))}
          <button
            type="button"
            className="nav-item nav-item--brain"
            onClick={() => openChatWith(null)}
            aria-label={isReviewMode ? 'Ask Brain — speak or type' : 'Open Brain chat — speak or type'}
          >
            <BrainOrb size="sm" ariaLabel="Brain" />
            <span className="nav-item__label">{isReviewMode ? 'Ask Brain' : 'Brain'}</span>
            <span className="nav-kbd">⌘K</span>
          </button>
        </nav>
        <div className="sidebar__foot">
          <NavLink to="/settings" className="coach-card">
            <div className="coach-avatar">{initials}</div>
            <div className="coach-meta">
              <div className="coach-name">{user?.email ?? 'Coach'}</div>
              <div className="coach-team">{teamLabel}</div>
            </div>
            <NavIcon name="settings" />
          </NavLink>
        </div>
      </aside>

      <Outlet />

      {/* Pro-coach review banner — fixed corner pill that appears only
          when the current user is a review-mode coach. No-op otherwise. */}
      <CoachReviewBanner />

      {/* First-run tour overlay — fullscreen guided tour for review
          coaches on first arrival (or `?replay=1`). Mounted at shell
          level so it survives the navigation between steps. */}
      {showFirstRunTour && <FirstRunTour />}

      {/* Persistent Ask-AI affordance — visible on every page. Click to
       *  open the chat panel; ⌘K still opens the CommandBar. The orb sits
       *  in the bottom-right above the page content but below the command
       *  bar / chat panel scrim. */}
      <button
        type="button"
        className="brain-floater"
        onClick={() => openChatWith(null)}
        aria-label="Ask AI"
        title="Ask AI — speak or type. ⌘K opens the command bar."
      >
        <BrainOrb size="md" ariaLabel="Brain" />
        <span className="brain-floater__caption">Ask AI</span>
      </button>

      <CommandBar
        open={open}
        onClose={() => setOpen(false)}
        defaults={commands}
        onAskBrain={(seed) => {
          setOpen(false);
          openChatWith(seed);
        }}
      />
      <BrainChatPanel
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        seedMessage={chatSeed}
        onSeedConsumed={() => setChatSeed(null)}
      />
      <NowSurface open={nowOpen} onClose={() => setNowOpen(false)} />
    </div>
  );
}
